// Lib
import { Dispatch, FC, SetStateAction, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  closestCorners,
  DndContext,
  DragOverEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  rectSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
// Api
import { useUpdateLazyProductMediaMutation } from "rtkQuery/query/mediaAPI";
// Hooks
import { useNotification } from "hooks";
// Types
import { MediaItem } from "../../types";
// Helpers
// Utils
import { errorHandler } from "utils/errorHandler";
// Icons
import { DescriptionBlackIcon, TrashIcon } from "icons";
// Components
import { MediaContentCard } from "../MediaContentCard";
import { ConfirmDialog } from "components/Modals";
// Styled
import { StyledGrid, StyledSkeletonImage } from "./styled";
import { NoItemsContent } from "components";

interface MediaContentProps {
  isMediaFetching: boolean;
  items: MediaItem[];
  setItems: Dispatch<SetStateAction<MediaItem[]>>;
}

export const MediaContent: FC<MediaContentProps> = ({
  isMediaFetching,
  items,
  setItems,
}) => {
  const { providerId } = useParams();
  const { openNotification } = useNotification();

  const [update, { isLoading, error }] = useUpdateLazyProductMediaMutation();

  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  useEffect(() => {
    if (!providerId) {
      openNotification({
        type: "error",
        message: "Provider ID is required to update media.",
      });
      return;
    }
  }, [providerId, openNotification]);

  useEffect(() => {
    if (error) {
      errorHandler({ error, openNotification });
    }
  }, [error]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleUpdateOrder = async (media: MediaItem[]) => {
    try {
      const orderedItems = media.map((item, index) => {
        const { imageURL, posterURL, videoURL } = item || {};

        return {
          imageURL,
          posterURL,
          videoURL,
          order: index,
        };
      });

      await update({ providerId, media: orderedItems }).unwrap();

      openNotification({ message: "Media order updated successfully." });
    } catch (error) {
      errorHandler({ error, openNotification });
    }
  };

  const handleDelete = async (id: string) => {
    try {
      const updatedItems = items.filter(item => item.id !== id);

      const orderedItems = updatedItems.map((item, index) => ({
        imageURL: item.imageURL,
        posterURL: item.posterURL,
        videoURL: item.videoURL,
        order: index,
      }));

      await update({ providerId, media: orderedItems }).unwrap();

      setItems(updatedItems);

      setConfirmDeleteId(null);

      openNotification({ message: "Media deleted successfully." });
    } catch (error) {
      errorHandler({ error, openNotification });
    }
  };

  const getIndex = (id: string) => items.findIndex(item => item.id === id);

  const handleDragEnd = (event: DragOverEvent) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      const oldIndex = getIndex(String(active.id));
      const newIndex = getIndex(String(over.id));
      setItems(items => {
        return arrayMove(items, oldIndex, newIndex);
      });

      handleUpdateOrder(arrayMove(items, oldIndex, newIndex));
    }
  };

  if (isMediaFetching) {
    return (
      <StyledGrid>
        <StyledSkeletonImage active />
        <StyledSkeletonImage active />
        <StyledSkeletonImage active />
      </StyledGrid>
    );
  }

  if (!items || !items.length) {
    return (
      <NoItemsContent
        Icon={DescriptionBlackIcon}
        message="No Media to show."
        description="You can add media by clicking the 'Add Media' button."
      />
    );
  }

  return (
    <>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCorners}
        onDragEnd={handleDragEnd}
      >
        <StyledGrid>
          <SortableContext items={items} strategy={rectSortingStrategy}>
            {items.map(item => (
              <MediaContentCard
                key={item?.id}
                media={item}
                onDelete={() => setConfirmDeleteId(item?.id)}
              />
            ))}
          </SortableContext>
        </StyledGrid>
      </DndContext>

      <ConfirmDialog
        open={!!confirmDeleteId}
        isLoading={isLoading}
        Icon={TrashIcon}
        message="This media will be deleted."
        description="Are you sure you want to delete this media?"
        onCancel={() => setConfirmDeleteId(null)}
        firstCTAButton={{
          title: "Delete Media",
          status: "danger",
          loading: isLoading,
          onClick: () => handleDelete(confirmDeleteId),
        }}
      />
    </>
  );
};
